import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CalendarModule } from 'primeng/calendar';
import { TimeKeepingService } from '../../services/time-keeping.service';
import { ToastService } from '../../services/toast.service';
import { ReportService } from '../../../modules/client/dashboard/reports/reports.service';
import { DateTimeService } from '../../services/date-time.service';
import { PipesModule } from '../../pipes/pipes.module';
import moment from 'moment';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-timesheet-create',
  standalone: true,
  imports: [
    CalendarModule,
    InputTextareaModule,
    FloatLabelModule,
    ButtonModule,
    FormsModule,
    InputTextModule,
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    PipesModule,
  ],
  templateUrl: './timesheet-create.component.html',
  styleUrl: './timesheet-create.component.scss',
})
export class TimesheetCreateComponent implements OnInit {
  /** Firm ID – required to fetch projects and build the API payload */
  @Input() firmId: string = '';
  /** Comma-separated market IDs used to filter the project list */
  @Input() searchMarkets: string = '';
  /** Pre-selected timezone passed from the parent */
  @Input() defaultTimezone: any = null;

  @Output() closeModal: EventEmitter<any> = new EventEmitter<any>();

  createForm!: FormGroup;

  projectsList: any[] = [];
  rolesList: any[] = [];
  candidatesList: any[] = [];
  timezonesList: any[] = [];

  isLoadingProjects = false;
  isLoadingRoles = false;
  isLoadingCandidates = false;

  submitted = false;
  duration = '--:--';

  constructor(
    private fb: FormBuilder,
    private timeKeepingService: TimeKeepingService,
    private toast: ToastService,
    private reportService: ReportService,
    private dateTimeService: DateTimeService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.timezonesList = this.dateTimeService.getTimeZoneList(environment.usSpecificTimezoneOnly);

    if (this.defaultTimezone) {
      this.createForm.get('timezone')?.setValue(this.defaultTimezone);
    }

    this.createForm.valueChanges.subscribe(() => {
      this.duration = this.calculateDuration();
    });
    this.duration = this.calculateDuration();

    this.fetchProjects();
  }

  // ── Form ─────────────────────────────────────────────────────────────

  initForm(): void {
    this.createForm = this.fb.group({
      project:   [null, Validators.required],
      role:      [{ value: null, disabled: true }, Validators.required],
      candidate: [{ value: null, disabled: true }, Validators.required],
      timezone:  [this.defaultTimezone, Validators.required],
      start_date: [new Date(), Validators.required],
      start_time: [new Date(), Validators.required],
      end_date:   [new Date(), Validators.required],
      end_time:   [new Date(), Validators.required],
      notes:      [''],
    });
  }

  calculateDuration(): string {
    try {
      const v = this.createForm.getRawValue();
      const startDate: Date | null = v.start_date;
      const startTime: Date | null = v.start_time;
      const endDate: Date | null = v.end_date;
      const endTime: Date | null = v.end_time;

      if (!startDate || !startTime || !endDate || !endTime) return '--:--';

      const start = new Date(startDate);
      start.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

      const end = new Date(endDate);
      end.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

      const diffMs = end.getTime() - start.getTime();
      if (diffMs <= 0) return '--:--';

      const totalMinutes = Math.floor(diffMs / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    } catch {
      return '--:--';
    }
  }

  // Convenience getter so the template can access controls cleanly
  get f() {
    return this.createForm.controls;
  }

  isInvalid(controlName: string): boolean {
    const ctrl = this.createForm.get(controlName);
    return !!(ctrl && ctrl.invalid && (ctrl.touched || this.submitted));
  }

  // ── Data fetching ─────────────────────────────────────────────────────

  async fetchProjects(): Promise<void> {
    this.isLoadingProjects = true;
    try {
      const markets = this.searchMarkets ? this.searchMarkets.split(',') : [];
      this.projectsList = await this.reportService.getProjectsByFirmAndMarkets(
        this.firmId, markets, []
      );
    } catch {
      this.toast.showError('Failed to load projects');
    } finally {
      this.isLoadingProjects = false;
    }
  }

  async onProjectChange(event: any): Promise<void> {
    // Reset downstream controls
    this.rolesList = [];
    this.candidatesList = [];
    this.createForm.patchValue({ role: null, candidate: null });
    this.createForm.get('role')?.disable();
    this.createForm.get('candidate')?.disable();

    const project = event?.value;
    if (!project) return;

    await this.fetchRoles(project._id);
  }

  async fetchRoles(projectId: string): Promise<void> {
    this.isLoadingRoles = true;
    try {
      this.rolesList = await this.reportService.getAllRolesByProjectIds([projectId]);
      this.createForm.get('role')?.enable();
    } catch {
      this.toast.showError('Failed to load roles');
    } finally {
      this.isLoadingRoles = false;
    }
  }

  async onRoleChange(event: any): Promise<void> {
    // Reset candidate control
    this.candidatesList = [];
    this.createForm.patchValue({ candidate: null });
    this.createForm.get('candidate')?.disable();

    const role = event?.value;
    if (!role) return;

    const project = this.createForm.get('project')?.value;
    if (!project) return;

    await this.fetchCandidates(project._id, role.id);
  }

  async fetchCandidates(projectId: string, roleId: string): Promise<void> {
    this.isLoadingCandidates = true;
    try {
      const today = moment().format('MM/DD/YYYY');
      this.candidatesList = await this.reportService.getSelectedProjectReviewers(
        [projectId], [roleId], this.firmId, today, today
      );
      this.candidatesList.forEach((c: any) => {
        c.fullName = `${c.firstName} ${c.lastName}`;
      });
      this.createForm.get('candidate')?.enable();
    } catch {
      this.toast.showError('Failed to load candidates');
    } finally {
      this.isLoadingCandidates = false;
    }
  }

  // ── Submit ────────────────────────────────────────────────────────────

  onSubmit(): void {
    this.submitted = true;
    this.createForm.markAllAsTouched();

    // Re-enable disabled controls so getRawValue() captures them
    const raw = this.createForm.getRawValue();

    const hasErrors =
      !raw.project ||
      !raw.role ||
      !raw.candidate ||
      !raw.timezone ||
      !raw.start_date ||
      !raw.start_time ||
      !raw.end_date ||
      !raw.end_time;

    if (hasErrors || this.createForm.invalid) {
      return;
    }

    const tz = raw.timezone;
    const body: any = {
      end_date:  this.getMomentFormatByTimeZone(raw.end_date,   raw.end_time,   tz.id),
      start_date: this.getMomentFormatByTimeZone(raw.start_date, raw.start_time, tz.id),
      role_id:   raw.role.id,
      notes:     raw.notes || '',
      reviewerId: raw.candidate._id,
      timezone_abbrevation:  tz.val,
      timezone: tz.name.match(/UTC\s*([+-]\d{2}:\d{2})/)?.[1] ?? '',
    };

    this.timeKeepingService.addTSheetWithParams(body).subscribe({
      next: () => {
        this.toast.showSuccess('Time added Successfully');
        this.cancel(true);
      }
    });
  }

  cancel(data: any = false): void {
    this.closeModal.emit(data);
  }

  getMomentFormatByTimeZone(date: any, time: any, timezone: string): string {
    const timeZoneStr = timezone.split('[')[0];
    const momentDateByTimezone = moment.tz(timeZoneStr);
    const momentDate = moment(date, 'MM/DD/YYYY').isValid()
      ? moment(date, 'MM/DD/YYYY')
      : moment(date);
    const momentTime = moment(time);

    const originalDate = momentDate.date();

    momentDateByTimezone.set({
      year:   momentDate.year(),
      month:  momentDate.month(),
      date:   momentDate.date(),
      hour:   momentTime.hour(),
      minute: momentTime.minute(),
      second: 0,
    });

    // Correct for date roll-over caused by timezone conversion
    if (originalDate !== 1 && momentDateByTimezone.date() === 1) {
      const currentMonth = momentDateByTimezone.month();
      const currentYear  = momentDateByTimezone.year();

      if (currentMonth === 11) {
        momentDateByTimezone.month(0);
        momentDateByTimezone.year(currentYear + 1);
      } else {
        momentDateByTimezone.month(currentMonth + 1);
      }
    }

    return momentDateByTimezone.format();
  }
}
